import React from 'react';

import { tv } from '../../tv';

/**
 * Body text.
 *
 * The API is `size` + `weight` + `color` over closed enums rather than a
 * single named `variant`, which is what most current systems ship (Radix,
 * Primer and Atlassian have no variant at all; Polaris has one but makes it
 * optional). The constraint comes from closing the enums, not from collapsing
 * them into role names: three tight props admit fewer states than one long
 * role list plus the override props a role list always ends up needing.
 *
 * Each size resolves to one token carrying both its font size and its line
 * height, so a caller picks a step rather than pairing two values. Anything
 * off the scale fails to compile.
 *
 * Headings are a separate component. Visual size does not imply document
 * level, and the product already proves it: a page title here was written as
 * a Text with role and aria-level bolted on to fake a heading.
 *
 * `color` and `weight` have no default, so text takes them from whatever
 * encloses it. This is what every current system except one does, and the
 * reason is that a component which always emits a colour can never sit inside
 * a themed region: the emitted value wins over the region's, and the caller
 * has to remember an opt-out at every site. Inheriting makes the common case
 * correct without anyone remembering anything, and the uncommon case is still
 * one prop away.
 *
 * `size` is the exception and keeps its default, because a step binds a font
 * size to the line height that belongs with it. Inheriting a size would take
 * the parent's size with this element's line height, which is the pairing the
 * scale exists to prevent.
 *
 * The default is `sm`, 14px: the size of the product's UI text, the step
 * every control and container already carries, and the default of every
 * product design system surveyed (typography.md). `base`, 16px, is the
 * reading step, asked for by name on long-form content.
 */

const textStyles = tv({
  variants: {
    size: {
      xs: 'ck:text-xs',
      sm: 'ck:text-sm',
      base: 'ck:text-base',
      lg: 'ck:text-lg',
      /* Emits nothing, for text inside a region that already sets the step it
       * should read at. A table is the case that needs it: the cell names one
       * size and every column takes it, so the columns cannot drift apart.
       * The pairing the scale protects still holds, because the region set a
       * step from the same scale. */
      inherit: '',
    },
    weight: {
      regular: 'ck:font-regular',
      medium: 'ck:font-medium',
      semibold: 'ck:font-semibold',
      bold: 'ck:font-bold',
    },
    /* The status roles resolve to the same three tokens the rest of the system
     * already uses (FormError is on ck:text-error-text), so the palette has one
     * definition and changing it moves every consumer at once. They are roles
     * rather than hues: a call site says what the text means and never which
     * colour that is, which is what keeps light and dark in step.
     *
     * A status role is not the same thing as a measured value. A score that
     * falls in a band belongs to Score, which labels the band in words as well
     * as colour; reaching for `color="error"` to shade a number would put the
     * meaning back into the hue alone. */
    color: {
      /** Primary content. */
      default: 'ck:text-text-default',
      /** Secondary content: descriptions, supporting copy. */
      muted: 'ck:text-text-muted',
      /** Something went wrong: a failed validation, a rejected item. */
      error: 'ck:text-error-text',
      /** Something succeeded: an approval, a completed check. */
      success: 'ck:text-success-text',
      /** Something needs attention but has not failed. */
      warning: 'ck:text-warning-text',
      /** Inherits from an enclosing element that already sets a colour. */
      inherit: 'ck:text-inherit',
    },
    /* Alignment is a property of text, not of the box around it, so it lives
     * here rather than forcing a wrapper. Justified is absent: it produces
     * uneven word spacing and hurts readability. */
    align: {
      start: 'ck:text-left',
      center: 'ck:text-center',
      end: 'ck:text-right',
    },
    /* Whether a run of text may break across lines is a property of the text,
     * for the same reason alignment is. Without it a label that must stay on
     * one line needs a wrapper to carry the rule, and the wrapper then changes
     * the layout it was only meant to annotate: a name beside a sentence
     * became a block and stopped sitting on the same line. */
    isNoWrap: {
      true: 'ck:whitespace-nowrap ck:shrink-0',
      false: '',
    },
    /* Takes the space a flex parent has left over. Without it a run of text
     * that should push what follows to the far edge needs a wrapper to carry
     * the rule, and the wrapper is a block: it fills the width, so a centred
     * line inside it is centred against the wrapper rather than the parent,
     * and the alignment the parent asked for is quietly lost. min-w-0 is
     * required here: a flex item defaults to min-width:auto, so without it
     * long copy refuses to shrink below its intrinsic width and overflow
     * wins over grow. */
    isGrow: {
      true: 'ck:grow ck:min-w-0',
      false: '',
    },
    /* An eyebrow is the small uppercase label above a section or beside a
     * badge. It is one thing the product wrote thirteen different ways,
     * pairing a size, a weight, a tracking value and a transform by hand.
     * Uppercase genuinely needs the extra tracking to stay readable, which
     * is why the two travel together rather than being separate props. */
    isEyebrow: {
      true: 'ck:uppercase ck:tracking-wider',
      false: '',
    },
    /* Two ways to keep text short, kept apart because they behave
     * differently inside a flex row. `lines` clamps: the text wraps normally
     * and hides everything past the count, so its minimum width stays one
     * word and a flex parent can shrink it. `isTruncated` refuses to wrap
     * and cuts with an ellipsis wherever the space ends, which makes the
     * text's minimum width the whole string; min-w-0 lets the text itself
     * shrink, but a wrapper between it and the flex row cannot know to.
     * Conflating the two is how a clamped title started overflowing its
     * card. Prefer `lines` unless the cut genuinely must ignore word
     * boundaries, as dense table cells do.
     *
     * Both emit min-w-0. Flex items default to min-width:auto, and without
     * an explicit floor the clamp never engages: the item grows to the
     * string and there is nothing left to hide. */
    lines: {
      1: 'ck:line-clamp-1 ck:min-w-0 ck:max-w-full ck:whitespace-normal',
      2: 'ck:line-clamp-2 ck:min-w-0 ck:max-w-full ck:whitespace-normal',
      3: 'ck:line-clamp-3 ck:min-w-0 ck:max-w-full ck:whitespace-normal',
    },
    truncated: {
      true: 'ck:block ck:max-w-full ck:truncate ck:min-w-0',
      false: '',
    },
    /* A live label is the one animated text in the system: it says work is
     * happening without a spinner beside it. It is muted by construction, so
     * the shimmer band has a fixed relationship to the text in both modes,
     * and `color` is ignored while live. Under reduced motion the utility
     * renders plain muted text, which is the same information standing
     * still. */
    live: {
      true: 'ck:text-text-muted ck:animate-shimmer',
      false: '',
    },
  },
  /* Only size defaults. A default here emits a class, and a class is what
   * stops colour and weight being inherited, so those two are left unset. */
  defaultVariants: {
    size: 'sm',
    isEyebrow: false,
    isNoWrap: false,
    isGrow: false,
    truncated: false,
    live: false,
  },
});

export interface TextProps {
  children?: React.ReactNode;
  /** Step on the type scale. Each step carries its own line height. */
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'inherit';
  /** Inter ships at these four weights; there is no arbitrary numeric weight. */
  weight?: 'regular' | 'medium' | 'semibold' | 'bold';
  /** Emphasis, as a role rather than a colour value. */
  color?: 'default' | 'muted' | 'error' | 'success' | 'warning' | 'inherit';
  /**
   * The element to render. Presentational tags are deliberately absent: `b`
   * and `i` describe appearance the weight prop already owns, and `strong`
   * and `em` carry the meaning when it is real.
   */
  as?: 'p' | 'span' | 'div' | 'label' | 'strong' | 'em';
  /**
   * One line, cut with an ellipsis wherever the space ends, ignoring word
   * boundaries. For dense cells; prefer `lines={1}` elsewhere, which clamps
   * at a word boundary and shrinks correctly inside a flex row.
   */
  isTruncated?: boolean;
  /** Aligns the text within its own box. */
  align?: 'start' | 'center' | 'end';
  /** A small uppercase label, as used above a section or beside a badge. */
  isEyebrow?: boolean;
  /** Keeps the text on one line, for a label that must not break mid-phrase. */
  isNoWrap?: boolean;
  /** Takes the space a flex parent has left over. */
  isGrow?: boolean;
  /** Clamps to a number of lines, ending with an ellipsis. */
  lines?: 1 | 2 | 3;
  /**
   * Work is happening behind this label: an agent step still running, a
   * document still generating. The text shimmers, muted, and `color` is
   * ignored while it does. The only animated text in the system; under
   * reduced motion it is static muted text.
   */
  isLive?: boolean;
  id?: string;
  htmlFor?: string;
  /** Hides decorative text from assistive technology. */
  'aria-hidden'?: boolean;
  /** Native tooltip, which a truncated label needs so the full text stays reachable. */
  title?: string;
}

export const Text = React.forwardRef<HTMLElement, TextProps>(function Text(
  {
    children,
    size = 'sm',
    weight,
    color,
    as: As = 'p',
    isTruncated = false,
    align,
    lines,
    isEyebrow = false,
    isNoWrap = false,
    isGrow = false,
    isLive = false,
    ...props
  },
  ref,
) {
  /* An emphasis tag means the emphasis, so the styling follows from it rather
   * than having to be repeated as a prop. Without this the class beats the
   * browser's own bold and `as="strong"` renders regular: the markup says
   * emphasised and the pixels say otherwise. An explicit weight still wins,
   * so a caller can render a semantic strong at a different step. */
  const semanticWeight = As === 'strong' ? 'bold' : undefined;

  return (
    <As
      {...props}
      // A polymorphic element has no single ref type across p/span/label, so
      // the cast is confined here rather than pushed onto callers.
      ref={ref as React.Ref<never>}
      className={textStyles({
        size,
        weight: weight ?? semanticWeight,
        color: isLive ? undefined : color,
        align,
        isEyebrow,
        isNoWrap,
        isGrow,
        lines,
        truncated: isTruncated,
        live: isLive,
      })}
      data-component="text"
    >
      {children}
    </As>
  );
});
